import React, {useEffect, useState} from "react";
import {B2Booking} from "../../data/client.gen";
import {useFlag} from "@variocube/app-ui";
import {useBooking} from "../../context/booking";
import {useAsyncCallback} from "react-async-hook";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import {useLocalization} from "../../i18n";
import {useNavigate} from "react-router-dom";
import {getZoneDateTimeNow, parseZoneDateTimeFromInstant} from "../../dates-utils";

export function BookingExpirationCountDown({booking}: { booking: B2Booking }) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {cancel: cancelBooking} = useBooking();

	const [expiration, setExpiration] = useState('');
	const [cancelPrompt, setCancelPrompt, clearCancelPrompt] = useFlag(false);



	function computeTimeRemain(booking: B2Booking) {
		const expiredAfter = booking.site.cancelUnpaidBookingAfter ?? 60;
		const zoneId = booking.site.zoneId ?? 'UTC';
		const duration = getZoneDateTimeNow(zoneId)
			.until(
				parseZoneDateTimeFromInstant(booking.createdAt, zoneId)
					.add({ minutes: expiredAfter }),
				{ largestUnit: 'minutes' }
			);
		setExpiration(`${duration.minutes}:${duration.seconds}`);
		return duration;
	}

	useEffect(() => {
		computeTimeRemain(booking);
		const interval = setInterval(() => {
			const duration = computeTimeRemain(booking);
			if (duration.minutes <= 0 && duration.seconds <= 0) {
				clearInterval(interval);
			}
		}, 1000)
		return () => {
			clearInterval(interval)
		}
	}, [booking]);

	const {loading, error, execute} = useAsyncCallback(cancelBooking);

	async function handleCancelBooking() {
		await execute();
		clearCancelPrompt();
	}

	return (
		<>
			<Alert
				severity="warning"
				action={
					<Button
						sx={{ minWidth: 120 }}
						size="small"
						variant="contained"
						color="secondary"
						disableElevation
						onClick={setCancelPrompt}
					>
						{t('booking.cancel.button')}
					</Button>
				}
			>
				<Typography variant="body2">{t('payment.reminder', { expiration })}</Typography>
			</Alert>
			<Alert
				severity="warning"
				action={
					<Button
						sx={{
							minWidth: 120
						}}
						size="small"
						variant="contained"
						color="primary"
						disableElevation
						onClick={() => navigate(`/${booking.tenant.id}/bookings/${booking.id}/payment`)}
					>
						{t('actions.pay')}
					</Button>
				}
			>
				<Typography variant="body2">{t('payment.completePaymentPrompt')}</Typography>
			</Alert>
			<Dialog open={cancelPrompt}>
				<DialogTitle>{t('booking.cancel.title')}</DialogTitle>
				<DialogContent>
					{t('booking.cancel.message')}
					{error && (
						<Alert severity="error">{"Failed to cancel booking."}</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button size="small" variant="contained" color="primary" disabled={loading} onClick={handleCancelBooking}>{t('common.yes')}</Button>
					<Button size="small" color="primary" disabled={loading} onClick={clearCancelPrompt}>{t('common.no')}</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export function BoxesAccess({booking}: { booking: B2Booking }) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {refresh} = useBooking();

	function handleBoxesView() {
		// Why using refresh here?
		// - In case the user releases the lockers and want to go back to box list view,
		//   the booking object should be refreshed to get the current active occupancies
		//   before rendering them in the box list view again
		refresh()
			.catch(console.error)
			.finally(() => {
				navigate(`/${booking.tenant.id}/bookings/${booking.id}/boxes`);
			});
	}

	return (
		<>
			<Alert
				severity="info"
				action={
					<Button
						size="small"
						variant="contained"
						color="primary"
						disableElevation
						onClick={handleBoxesView}
					>
						{t('boxes.view')}
					</Button>
				}
			>
				<Typography variant="body2">{t('booking.active')}</Typography>
			</Alert>
			{booking.siteHasMultipleCubes && (
				<Alert severity="info">
					<Typography variant="body2">{t('sites.siteHasMultipleCubes')}</Typography>
				</Alert>
			)}
		</>
	)
}

export function BookingCancelHint() {
	const {t} = useLocalization();
	return (
		<Alert severity="warning">
			<Typography variant="body2">{t('booking.canceled')}</Typography>
		</Alert>
	)
}

export function BookingProlongationButton({booking}: { booking: B2Booking }) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	return (
		<Alert
			severity="warning"
			action={
				<Button
					size="small"
					variant="contained"
					color="primary"
					disableElevation
					onClick={() => navigate(`/${booking.tenant.id}/bookings/${booking.id}/prolong`)}
				>
					{t('booking.prolong.button')}
				</Button>
			}
		>
			<Typography variant="body2">{t('booking.prolong.message')}</Typography>
		</Alert>
	)
}

export function PendingPaymentButton({booking}: { booking: B2Booking }) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	return (
		<Alert
			severity="warning"
			action={
				<Button
					size="small"
					variant="contained"
					color="primary"
					disableElevation
					onClick={() => navigate(`/${booking.tenant.id}/bookings/${booking.id}/payment`)}
				>
					{t('actions.pay')}
				</Button>
			}
		>
			<Typography variant="body2">{t('payment.completePaymentPrompt')}</Typography>
		</Alert>
	)
}
